import type { Address } from '../types/interfaces/address.interface'

/**
 * Which maps app a link can open. There is no web API that names the visitor's default maps app: only Android
 * honours the standard `geo:` scheme, iOS opens Apple Maps from its own address, and a desktop browser has no handler
 * for either, so it gets a web map (docs/public-page.md, _The maps link_).
 */
export type MapsPlatform = 'android' | 'apple' | 'desktop'

export interface MapsPlatformHints {
  userAgent: string
  /** `navigator.maxTouchPoints`: what tells an iPad apart from the Mac it claims to be. */
  maxTouchPoints: number
}

export function mapsPlatform({ userAgent, maxTouchPoints }: MapsPlatformHints): MapsPlatform {
  if (/Android/i.test(userAgent)) return 'android'
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'apple'
  // iPadOS 13+ sends a desktop Safari user agent; a Mac has no touch points, an iPad has five.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'apple'
  return 'desktop'
}

/**
 * The address as a maps search: `Ulica 13, 193 00 Bratislava, Slovensko`. `''` without a street or a city - the
 * country alone, which the settings form fills in by default, would only show a map of the country.
 */
export function mapsQuery(address: Address): string {
  const street = address.addressLine1.trim()
  const city = address.city.trim()
  if (!street && !city) return ''
  const town = [address.zip.trim(), city].filter(Boolean).join(' ')
  return [street, town, address.state.trim()].filter(Boolean).join(', ')
}

/**
 * A link that opens the address in the platform's maps app, or `null` when there is no address to show. Built from
 * the address alone: no coordinates are stored. `geo:` must never reach a desktop browser, where it does nothing.
 */
export function mapsLinkFor(address: Address, platform: MapsPlatform): string | null {
  const query = mapsQuery(address)
  if (!query) return null
  const q = encodeURIComponent(query)
  switch (platform) {
    case 'android':
      return `geo:0,0?q=${q}`
    case 'apple':
      return `https://maps.apple.com/?q=${q}`
    case 'desktop':
      return `https://www.google.com/maps/search/?api=1&query=${q}`
  }
}
